import { Box, DialogActions, FormHelperText, Grid } from "@mui/material"
import Button from "@mui/material/Button"
import { ChangeEvent, useRef, useState } from "react"
import { ProductDto } from "@custom-types/products"
import { usePutNodeTagsMutation } from "@store/services/productsService"
import Dialog from "@components/Dialog"
import NodeTagsForm, {
  NODE_TAGS_FORM_ID,
  NodeTagsFormRef,
  NodeTagsFormValues,
} from "@features/administration/products/EditProduct/NodeTagsForm/NodeTagsForm"
import { formValuesToNodeTagsFileContent } from "@features/administration/products/EditProduct/nodeTagsHelper"
import { SubmitErrorHandler, SubmitHandler } from "react-hook-form"
import { enqueueSnackbar } from "notistack"
import TopActionsBar from "@features/administration/products/EditProduct/TopActionsBar"

interface Props {
  onClose: () => void
  product: ProductDto
}

export default function EditNodeTagsDialog({ onClose, product }: Props) {
  const [globalErrorMessages, setGlobalErrorMessages] = useState<string[]>([])
  const formRef = useRef<NodeTagsFormRef>(null)
  const [putNodeTags] = usePutNodeTagsMutation()

  const handleAddTagRow = () => {
    formRef.current?.addTag({ name: "", type: "dropdown", value: "", description: "" })
  }

  const handleSubmit: SubmitHandler<NodeTagsFormValues> = async (values) => {
    try {
      await putNodeTags({
        key: product.key,
        orgKey: product.organisation_key,
        divKey: product.division_key,
        data: formValuesToNodeTagsFileContent(values),
      }).unwrap()
      onClose()
      enqueueSnackbar("Node level tags saved successfully", { variant: "success" })
    } catch (error) {
      enqueueSnackbar("Something went wrong while saving the node level tags", { variant: "error" })
    }
  }

  const handleSubmitError: SubmitErrorHandler<NodeTagsFormValues> = (errors) => {
    const rows = errors?.rows ?? {}
    const errorRowsNumbers = Object.keys(rows)
      .filter((key) => key !== "root")
      .map((key) => Number(key) + 1)

    const messages: string[] = []

    if (rows.root?.message) {
      messages.push(rows.root.message)
    }

    if (errorRowsNumbers.length) {
      messages.push(`There are errors in rows: ${errorRowsNumbers.join(", ")}`)
    }

    setGlobalErrorMessages(messages)
  }

  const handleCancel = () => onClose()

  const handleImport = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    formRef.current?.importTagsFile(file)
  }

  const handleExport = () => formRef.current?.exportTagsFile()

  return (
    <Dialog
      title="Edit node tags"
      actionsComponent={
        <DialogActions sx={{ width: "100%", p: 0 }}>
          <Box sx={{ flexGrow: 1 }}>
            {globalErrorMessages.map((error, index) => (
              <FormHelperText error key={`${error}-${index}`}>
                - {error}
              </FormHelperText>
            ))}
          </Box>

          <Button color="error" variant="contained" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" form={NODE_TAGS_FORM_ID}>
            Save
          </Button>
        </DialogActions>
      }
      open
      onClose={onClose}
      disableEscapeKeyDown
      fullWidth
      maxWidth="lg"
    >
      <Grid container gap={1} direction="column">
        <Grid item>
          <TopActionsBar
            onAddTagRowClick={handleAddTagRow}
            onImportClick={handleImport}
            onExportClick={handleExport}
          />
        </Grid>

        <Grid item>
          <NodeTagsForm
            ref={formRef}
            product={product}
            onSubmit={handleSubmit}
            onSubmitError={handleSubmitError}
          />
        </Grid>
      </Grid>
    </Dialog>
  )
}
